import React, { useRef, useState, useEffect } from 'react';
import { RefreshCw, Link2, Database } from 'lucide-react';
import { Button, AlertMessage, Modal } from '../common/ui';
import { useAccountingActions } from '../../hooks/useAccountingActions';

const IS_DEV = process.env.NODE_ENV === 'development';

interface UseAccountingActionBarReturn {
  actionButtons: React.ReactElement;
  actionAlerts: React.ReactElement;
}

export function useAccountingActionBar(refetchFn: () => void): UseAccountingActionBarReturn {
  const actions = useAccountingActions(refetchFn);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const csvOverwriteInputRef = useRef<HTMLInputElement>(null);
  const braintreeCsvInputRef = useRef<HTMLInputElement>(null);
  const braintreeCsvOverwriteInputRef = useRef<HTMLInputElement>(null);
  const paypalInputRef = useRef<HTMLInputElement>(null);
  const vascoInputRef = useRef<HTMLInputElement>(null);

  const [reconcileOpen, setReconcileOpen] = useState(false);
  const reconcileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (reconcileRef.current && !reconcileRef.current.contains(e.target as Node)) {
        setReconcileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleFileInput = (ref: React.RefObject<HTMLInputElement | null>, handler: (file: File) => Promise<void>) =>
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      await handler(file);
      if (ref.current) ref.current.value = '';
    };

  // "Sync" and the "Reconcile ▾" menu never run at the same time.
  const busy = actions.reconciling || actions.matching || actions.uploading || actions.syncing;

  const revolutApiEnabled = actions.revolutApi?.enabled === true;
  const lastImport = actions.revolutApi?.lastImport;
  const syncTitle =
    'Sync orders, get Revolut payments, reconcile bank transfers and reconcile Revolut cards' +
    (revolutApiEnabled && lastImport
      ? `. Last Revolut payment import: ${new Date(lastImport.finishedAt).toLocaleString()}`
      : '');

  // "Revolut CC" uses the Revolut Merchant API when the server has it switched
  // on. Otherwise it opens the CSV file picker as before.
  const merchantApiEnabled = actions.revolutMerchantApi?.enabled === true;
  const merchantLastImport = actions.revolutMerchantApi?.lastImport;
  const revolutCcTitle = !merchantApiEnabled
    ? undefined
    : actions.revolutMerchantApi?.waiting
      ? 'Fetch card payments from the Revolut Merchant API. Revolut is preparing a report; click again to continue.'
      : merchantLastImport
        ? `Fetch card payments from the Revolut Merchant API. Last import: ${new Date(merchantLastImport.finishedAt).toLocaleString()}`
        : 'Fetch card payments from the Revolut Merchant API';

  const actionButtons = (
    <div className="flex gap-3">
      <input ref={fileInputRef} type="file" accept=".xml" className="hidden"
        onChange={handleFileInput(fileInputRef, actions.handleUploadXml)} />
      <input ref={csvInputRef} type="file" accept=".csv" className="hidden"
        onChange={handleFileInput(csvInputRef, actions.handleReconcileRevolut)} />
      <input ref={csvOverwriteInputRef} type="file" accept=".csv" className="hidden"
        onChange={handleFileInput(csvOverwriteInputRef, actions.handleReconcileRevolutOverwrite)} />
      <input ref={braintreeCsvInputRef} type="file" accept=".csv" className="hidden"
        onChange={handleFileInput(braintreeCsvInputRef, actions.handleReconcileBraintree)} />
      <input ref={braintreeCsvOverwriteInputRef} type="file" accept=".csv" className="hidden"
        onChange={handleFileInput(braintreeCsvOverwriteInputRef, actions.handleReconcileBraintreeOverwrite)} />
      <input ref={paypalInputRef} type="file" accept=".txt,.csv,.tsv" className="hidden"
        onChange={handleFileInput(paypalInputRef, actions.handleReconcilePaypal)} />
      <input ref={vascoInputRef} type="file" accept=".txt,.csv,.tsv" className="hidden"
        onChange={handleFileInput(vascoInputRef, actions.handleReconcileVasco)} />

      <div className="relative" ref={reconcileRef}>
        <Button variant="secondary" onClick={() => setReconcileOpen((o) => !o)}
          icon={Link2} disabled={busy}>
          {actions.reconciling || actions.matching || actions.uploading ? 'Reconciling…' : 'Reconcile ▾'}
        </Button>
        {reconcileOpen && (
          <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20">
            <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-md"
              title="Import a Revolut camt.053 XML statement file. Use it when the Revolut API is switched off or fails."
              onClick={() => { setReconcileOpen(false); fileInputRef.current?.click(); }}>
              Revolut statement (XML file)
            </button>
            <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => { setReconcileOpen(false); actions.handleBankTransferMatch(); }}>
              Bank transfers
            </button>
            <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              title={revolutCcTitle}
              onClick={() => {
                setReconcileOpen(false);
                if (merchantApiEnabled) actions.handleRevolutMerchantImport();
                else csvInputRef.current?.click();
              }}>
              Revolut CC
            </button>
            {merchantApiEnabled && (
              <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                title="Import a Revolut Merchant reconciliation statement CSV file instead (fallback)"
                onClick={() => { setReconcileOpen(false); csvInputRef.current?.click(); }}>
                Revolut CC (CSV file)
              </button>
            )}
            <button className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50"
              title="Wipes existing Revolut CC links in the CSV's date range, then re-imports. Asks for confirmation first."
              onClick={() => { setReconcileOpen(false); csvOverwriteInputRef.current?.click(); }}>
              Revolut CC (overwrite)
            </button>
            <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => { setReconcileOpen(false); braintreeCsvInputRef.current?.click(); }}>
              Braintree AIB
            </button>
            <button className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50"
              title="Wipes existing Braintree AIB links in the CSV's settlement-date range (±3 days), then re-imports. Asks for confirmation first."
              onClick={() => { setReconcileOpen(false); braintreeCsvOverwriteInputRef.current?.click(); }}>
              Braintree AIB (overwrite)
            </button>
            <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => { setReconcileOpen(false); paypalInputRef.current?.click(); }}>
              PayPal
            </button>
            <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-md"
              title="Imports a Vasco export (tab-delimited) and stores each order's Vasco invoice number (RACUN/LETO). Negative-amount rows are stored as credit notes (dobropis)."
              onClick={() => { setReconcileOpen(false); vascoInputRef.current?.click(); }}>
              Vasco
            </button>
          </div>
        )}
      </div>

      <Button variant="primary" onClick={actions.handleSync}
        disabled={busy} icon={RefreshCw} title={syncTitle}
        iconClassName={actions.syncing ? 'animate-spin' : ''}>
        {actions.syncing ? 'Syncing…' : 'Sync'}
      </Button>

      {IS_DEV && (
        <Button
          variant="secondary"
          onClick={actions.handleBackfillOrders}
          disabled={actions.backfilling || actions.syncing}
          icon={Database}
          title="Backfill B2B orders from 2025-09-01"
        >
          {actions.backfilling ? 'Backfilling…' : 'Backfill Sep 2025+'}
        </Button>
      )}
    </div>
  );

  const actionAlerts = (
    <>
      {/* whitespace-pre-line shows each step of "Sync" and each note of the Revolut card import on its own line. */}
      {actions.success && (
        <AlertMessage type="success" message={actions.success} title="" className="whitespace-pre-line"
          onDismiss={() => actions.setSuccess('')} dismissible>{''}</AlertMessage>
      )}
      {actions.warning && (
        <AlertMessage type="warning" message={actions.warning} title="" className="whitespace-pre-line"
          onDismiss={() => actions.setWarning('')} dismissible>{''}</AlertMessage>
      )}
      {actions.error && (
        <AlertMessage type="error" message={actions.error} title="" className="whitespace-pre-line"
          onDismiss={() => actions.setError('')} dismissible>{''}</AlertMessage>
      )}
      {actions.overwrite && (
        <Modal isOpen onClose={actions.cancelOverwrite} title={actions.overwrite.title} size="md">
          {actions.overwrite.phase === 'confirm' ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{actions.overwrite.message}</p>
              <div className="flex justify-end space-x-3">
                <button onClick={actions.cancelOverwrite}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={actions.confirmOverwrite}
                  className="px-4 py-2 rounded-md text-white bg-red-600 hover:bg-red-700">
                  Overwrite
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {actions.overwrite.phase === 'previewing' ? 'Analyzing CSV…' : 'Overwriting…'}{' '}
                {actions.overwrite.pct}%
              </p>
              <div className="w-full bg-gray-200 rounded h-2 overflow-hidden">
                <div
                  className="h-2 bg-naviter-blue transition-all duration-200 ease-out"
                  style={{ width: `${actions.overwrite.pct}%` }}
                />
              </div>
              <p className="text-xs text-gray-400">Estimated — the import is running on the server.</p>
            </div>
          )}
        </Modal>
      )}
    </>
  );

  return { actionButtons, actionAlerts };
}
